import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  faCalendar,
  faEnvelope,
  faList,
  faLock,
  faPaperPlane,
  faPercentage,
  faPrint,
  faSave,
  faUnlock,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { KitchenService } from 'src/app/services/configurators-2d/applications/kitchen.service';
import { WorkbenchService } from 'src/app/services/configurators-2d/applications/workbench.service';
import { CurrentService } from 'src/app/services/current.service';
import { CustomerService } from 'src/app/services/customer.service';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { ProcessQuoteService } from 'src/app/services/process-quote.service';
import { ProjectService } from 'src/app/services/project.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { TunnelService } from 'src/app/services/tunnel.service';
import { VALIDATION_COMPONENT_TYPE_ENUM } from '../../validation.component';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { cloneDeep } from 'src/app/services/helpers/utils';
import { PdfViewModel } from 'src/app/models/pdf-view-model';
import { PrintService } from 'src/app/services/print.service';
import { ToastService } from 'src/app/services/toast.service';
import { KitchenValidationSideBarActionsModel } from 'src/app/models/configurators-2d/applications/kitchen/validation/kitchen-validation-models';
import {
  KitchenFinancementDto,
  KitchenInstallationDto,
} from '../../../../models/configurators-2d/applications/kitchen/kitchen-pdf-dtos';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-kitchen-validation-side-bar-actions',
  templateUrl: './validation-side-bar-actions.component.html',
})
export class KitchenValidationSideBarActionsComponent implements OnInit, OnDestroy {
  @Input() model$: Observable<KitchenValidationSideBarActionsModel>;
  private _modelSubscription: Subscription;

  @Input() loading$: Observable<boolean>;
  private _loadingSubscription: Subscription;

  @Output() onRefreshRequested: EventEmitter<VALIDATION_COMPONENT_TYPE_ENUM> = new EventEmitter();

  public model: KitchenValidationSideBarActionsModel;
  private loading: boolean;

  private sendingToComPlusLoading: boolean;
  private saveLoading: boolean;
  public printLoading: boolean;

  private discountValueSelected: BehaviorSubject<DiscountTypeEnum> = new BehaviorSubject<DiscountTypeEnum>(undefined);
  private _discountValueSubscription: Subscription;

  //ICON
  faPrint = faPrint;
  faPaperPlane = faPaperPlane;
  faCalendar = faCalendar;
  faSave = faSave;
  faList = faList;
  faEnvelope = faEnvelope;
  faLock = faLock;
  faPercentage = faPercentage;
  faUnlock = faUnlock;

  get buttonsLoading() {
    return this.sendingToComPlusLoading || this.saveLoading || this.loading;
  }

  get locked(): boolean {
    return this.model?.locked;
  }

  get isPrevisitWorkbench() {
    const tunnelService = this.injector.get(TunnelService);
    return tunnelService.workbenchTunnelData.isCosentino;
  }

  constructor(public readonly injector: Injector) { }

  protected readonly featureSettingsService = this.injector.get(FeatureSettingsService);

  async ngOnInit(): Promise<void> {
    this.subscribeToObservables();
  }

  ngOnDestroy(): void {
    this._modelSubscription?.unsubscribe();
    this._loadingSubscription?.unsubscribe();
    this._discountValueSubscription?.unsubscribe();
  }

  private async loadFundingIfNeeded(): Promise<void> {
    if (!this.featureSettingsService.financementEnabled) {
      return;
    }

    if (this.checkFundingIsUndefined()) {
      const tunnelService = this.injector.get(TunnelService);
      await this.handleNewDiscountFunding(tunnelService.kitchenTunnelData.discountValue);
      this.onRefreshRequested.emit();
    }
  }

  private async sendKitchenComPlus(xml: string): Promise<number> {
    const translateService = this.injector.get(TranslateService);
    const processQuoteService = this.injector.get(ProcessQuoteService);

    let opportunityNumber;
    try {
      opportunityNumber = await processQuoteService.sendDotComPlusOrder(xml);
      return opportunityNumber;
    } catch (error: any) {
      throw translateService.instant('validation.com_plus_error.kitchen_send_error', {
        error: JSON.stringify(error),
      });
    }
  }

  async sendToComPlus() {
    const currentService = this.injector.get(CurrentService);
    const projectService = this.injector.get(ProjectService);
    const tunnelService = this.injector.get(TunnelService);
    const modalService = this.injector.get(ModalService);
    const kitchenService = this.injector.get(KitchenService);

    const project = currentService.HBMProject;
    const res = await kitchenService.generateXML(project.uuid);

    let kitchenOpportunityNumber: number;
    this.sendingToComPlusLoading = true;
    try {
      kitchenOpportunityNumber = await this.sendKitchenComPlus(res.xml);
    } catch (error) {
      this.sendingToComPlusLoading = false;
      this.handleError(JSON.stringify(error));
      return;
    }

    tunnelService.kitchenTunnelData.opportunityNumber = kitchenOpportunityNumber;

    let workbenchOpportunityNumber;
    if (tunnelService.kitchenTunnelData.workbenchProjectUUID) {
      try {
        workbenchOpportunityNumber = await this.sendWorkbenchConfigToCOMPlus();
      } catch (error: any) {
        this.sendingToComPlusLoading = false;
        this.handleError(JSON.stringify(error));
        return;
      }
    }

    tunnelService.kitchenTunnelData.workbenchOpportunityNumber = workbenchOpportunityNumber;

    try {
      await projectService.lock(currentService.HBMProject.uuid);
      currentService.HBMProject.locked = true;
    } catch (error: any) {
      this.sendingToComPlusLoading = false;
      this.handleError(JSON.stringify(error));
    }

    try {
      await this.save(false);
    } catch (error) {
      this.sendingToComPlusLoading = false;
      this.handleError(JSON.stringify(error));
    }

    this.sendingToComPlusLoading = false;
    modalService.display(MODAL.ORDER_CONFIRM, {
      opportunityNumber: kitchenOpportunityNumber,
      workbenchOpportunityNumber: workbenchOpportunityNumber,
    });

    this.onRefreshRequested.emit();
  }

  private displaySaveModal(titleRef: string) {
    const modalService = this.injector.get(ModalService);

    modalService.display(MODAL.MESSAGE, {
      titleRef: titleRef,
      contentRef: 'Mise à jour effectuée',
      buttons: [
        {
          labelRef: 'Ok',
          primary: true,
          action: () => {
            modalService.close();
            this.sendSavingCountStatistic();
          },
        },
      ],
    });
  }

  private async updateConfiguration(requestContent: any) {
    const kitchenService = this.injector.get(KitchenService);
    try {
      //await kitchenService.putConfiguration(requestContent);
    } catch (error) {
      throw JSON.stringify(error);
    }
  }

  private async saveConfiguration(requestContent: any) {
    const kitchenService = this.injector.get(KitchenService);
    try {
      //await kitchenService.postConfiguration(requestContent);
    } catch (error) {
      throw JSON.stringify(error);
    }
  }

  async save(withModal: boolean = true) {
    this.saveLoading = true;
    const tunnelService = this.injector.get(TunnelService);
    let requestContent = this.buildKitchenTunnelData();
    if (tunnelService.kitchenTunnelData.configId) {
      requestContent.id = tunnelService.kitchenTunnelData.configId;
      try {
        await this.updateConfiguration(requestContent);
        if (withModal) {
          this.showSuccessToast('Succès de la mise à jour');
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.saveLoading = false;
      }
    } else {
      try {
        await this.saveConfiguration(requestContent);
        if (withModal) {
          this.showSuccessToast('Succès de la sauvegarde');
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.saveLoading = false;
      }
    }
  }

  private handleError(errorMessage: string) {
    logError(errorMessage);
    this.showErrorToast(errorMessage);
  }

  private buildKitchenTunnelData() {
    const currentService = this.injector.get(CurrentService);
    const tunnelService = this.injector.get(TunnelService);
    const customerService = this.injector.get(CustomerService);

    const discountValue = tunnelService.kitchenTunnelData.discountValue;

    const kitchenTunnelData: any = {
      projectUUID: currentService.HBMProject.uuid,
      isLess2Years: tunnelService.kitchenTunnelData.isLess2Years,
      withInstallation: tunnelService.kitchenTunnelData.withInstallation,
      downPayment: tunnelService.kitchenTunnelData.downPayment,
      discount: parseInt(discountValue.match(/\d+/)[0], 10),

      mensuality: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.fundingWithInstallation.mensuality
        : tunnelService.kitchenTunnelData.fundingWithoutInstallation.mensuality,
      otherMensuality: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.fundingWithInstallation.mensuality
        : tunnelService.kitchenTunnelData.fundingWithoutInstallation.mensuality,

      duration: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.fundingWithInstallation.duration
        : tunnelService.kitchenTunnelData.fundingWithoutInstallation.duration,
      otherDuration: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.fundingWithInstallation.duration
        : tunnelService.kitchenTunnelData.fundingWithoutInstallation.duration,

      productPrice: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.productPrice
        : tunnelService.kitchenTunnelData.productPriceWithoutInstallation,
      otherProductPrice: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.productPrice
        : tunnelService.kitchenTunnelData.productPriceWithoutInstallation,

      appliancePrice: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.appliancePrice
        : tunnelService.kitchenTunnelData.appliancePriceWithoutInstallation,
      otherAppliancePrice: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.appliancePrice
        : tunnelService.kitchenTunnelData.appliancePriceWithoutInstallation,

      totalProductPrice: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.totalProductPrice
        : tunnelService.kitchenTunnelData.totalProductPriceWithoutInstallation,
      otherTotalProductPrice: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.totalProductPrice
        : tunnelService.kitchenTunnelData.totalProductPriceWithoutInstallation,

      installationPrice: tunnelService.kitchenTunnelData.installationPrice,
      taeg: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.taegWithInstallation
        : tunnelService.kitchenTunnelData.taegWithoutInstallation,

      montantTotal: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.projectTotalPrice
        : tunnelService.kitchenTunnelData.projectTotalWithoutInstallationPrice,
      otherMontantTotal: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.projectTotalPrice
        : tunnelService.kitchenTunnelData.projectTotalWithoutInstallationPrice,

      title: currentService.HBMProject.name,
      customerId: customerService.userDatas.id,
      creatorId: currentService.vendor.sapId,

      bom: JSON.stringify(tunnelService.kitchenTunnelData.bom),
      installationDetail: JSON.stringify(tunnelService.kitchenTunnelData.detailTable),

      offers: JSON.stringify(
        tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.offerTableWithInstallation
          : tunnelService.kitchenTunnelData.offerTableWithoutInstallation
      ),
      otherOffers: JSON.stringify(
        !tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.offerTableWithInstallation
          : tunnelService.kitchenTunnelData.offerTableWithoutInstallation
      ),
      txtAssuranceWithInstallation: tunnelService.kitchenTunnelData.txtAssuranceWithInstallation,
      vacWithInstallation: tunnelService.kitchenTunnelData.vacWithInstallation,
      vacWithoutInstallation: tunnelService.kitchenTunnelData.vacWithoutInstallation,
      txtAssuranceWithoutInstallation: tunnelService.kitchenTunnelData.txtAssuranceWithoutInstallation,
      styleId: currentService.kitchen.styleId,
      modelId: currentService.kitchen.modelId,
      layoutRef: currentService.kitchen.layoutRef,
      ovenConfigurationRef: currentService.kitchen.ovenConfigurationRef,
      fridgeConfigurationRef: currentService.kitchen.fridgeConfigurationRef,
      interestWithInstallation: tunnelService.kitchenTunnelData.fundingWithInstallation.interest,
      interestWithoutInstallation: tunnelService.kitchenTunnelData.fundingWithoutInstallation.interest,

      mlData: JSON.stringify(
        tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.mlDataWithInstallation
          : tunnelService.kitchenTunnelData.mlDataWithoutInstallation
      ),
      otherMlData: JSON.stringify(
        !tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.mlDataWithInstallation
          : tunnelService.kitchenTunnelData.mlDataWithoutInstallation
      ),
      opportunityNumber: tunnelService.kitchenTunnelData.opportunityNumber,
      workbenchProjectUUID: tunnelService.kitchenTunnelData.workbenchProjectUUID,
      workbenchOpportunityNumber: tunnelService.kitchenTunnelData.workbenchOpportunityNumber,
    };

    return kitchenTunnelData;
  }

  private async sendWorkbenchConfigToCOMPlus() {
    const tunnelService = this.injector.get(TunnelService);

    if (tunnelService.kitchenTunnelData.workbenchProjectUUID) {
      const translateService = this.injector.get(TranslateService);
      const workbenchService = this.injector.get(WorkbenchService);
      let res: { orderId: string, xml: string };
      try {
        res = await workbenchService.generateXML(tunnelService.workbenchTunnelData.id);
      } catch (error: any) {
        this.sendingToComPlusLoading = false;
        throw new Error(
          translateService.instant('validation.com_plus_error.workbench_create_error', {
            error: JSON.stringify(error),
          })
        );
      }

      try {
        const processQuoteService = this.injector.get(ProcessQuoteService);
        const workbenchOpportunityNumber: number = await processQuoteService.sendDotComPlusOrder(res.xml);
        //Dans le cas ou on veut envoyer à com+ et qu'il y a pas de config de chargée (skip config)
        if (!workbenchService.configuration?.id) {
          await workbenchService.getProjectById(tunnelService.workbenchTunnelData.id, true);
        }
        await workbenchService.updateOrderAndSaveProject(res.orderId, workbenchOpportunityNumber);
        this.sendWorkbenchComPlusStatistic();
        return workbenchOpportunityNumber;
      } catch (error: any) {
        this.sendingToComPlusLoading = false;
        throw new Error(
          translateService.instant('validation.com_plus_error.workbench_send_error', {
            error: JSON.stringify(error),
          })
        );
      }
    }
  }

  private sendWorkbenchComPlusStatistic() {
    const statisticService = this.injector.get(StatisticService);
    //Envoi de la stat de projet envoyé à COM+
    statisticService.sendStatistic(StatisticTypeEnum.KITCHEN_WORKBENCH_SENT_COMPLUS_COUNT, 1, CONFIG_CONTEXT.KITCHEN);
  }

  private sendSavingCountStatistic() {
    const statisticService = this.injector.get(StatisticService);
    //Envoi de la stat de sauvegarde de projet
    statisticService.sendStatistic(StatisticTypeEnum.SAVING_COUNT, 1, CONFIG_CONTEXT.KITCHEN);
  }

  private subscribeToObservables() {
    this._modelSubscription = this.model$.subscribe(model => {
      if (model) {
        this.model = model;
        this.loadFundingIfNeeded();
      }
    });

    this._loadingSubscription = this.loading$.subscribe(loading => (this.loading = loading));
    this._discountValueSubscription = this.discountValueSelected
      .asObservable()
      .subscribe(async (discountValue: DiscountTypeEnum) => {
        if (discountValue) {
          const tunnelService = this.injector.get(TunnelService);
          tunnelService.kitchenTunnelData.discountValue = discountValue;

          await this.handleNewDiscountFunding(discountValue);
          this.onRefreshRequested.emit();
        }
      });
  }

  private async handleNewDiscountFunding(discountValue: DiscountTypeEnum) {
    if (!this.featureSettingsService.financementEnabled) {
      return;
    }

    this.loading = true;
    try {
      await this.loadWithoutInstallationFunding(discountValue);
      await this.loadWithInstallationFunding(discountValue);

      this.loading = false;
    } catch (error) {
      this.loading = false;
    }
  }

  private async loadWithoutInstallationFunding(discountValue: DiscountTypeEnum) {
    const tunnelService = this.injector.get(TunnelService);
    const montantReel =
      tunnelService.kitchenTunnelData.kitchenPricing.withoutInstallation.prixTotal.discountPrices[discountValue]
        .amount - tunnelService.kitchenTunnelData.downPayment;
    const requestContent = {
      montant: montantReel,
      duree: 10,
      duree_marge: 15,
    };
    await this.loadOffers(requestContent, false);
  }

  private async loadWithInstallationFunding(discountValue: DiscountTypeEnum) {
    const tunnelService = this.injector.get(TunnelService);
    const montantReel =
      tunnelService.kitchenTunnelData.kitchenPricing.withInstallation[this.priceKey].prixTotal.discountPrices[
        discountValue
      ].amount - tunnelService.kitchenTunnelData.downPayment;
    const requestContent = {
      montant: montantReel,
      duree: 10,
      duree_marge: 15,
    };
    await this.loadOffers(requestContent, true);
  }

  unlock() {
    const projectService = this.injector.get(ProjectService);
    const currentService = this.injector.get(CurrentService);
    try {
      projectService.unlock(currentService.HBMProject.id);
      currentService.HBMProject.locked = false;
      this.onRefreshRequested.emit(VALIDATION_COMPONENT_TYPE_ENUM.VALIDATION_SIDE_BAR_ACTION);
    } catch (error) {
      this.handleError(error);
    }
  }

  async orderPrevisitConfiguration() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    const currentService = this.injector.get(CurrentService);
    const kitchenService = this.injector.get(KitchenService);
    const translateService = this.injector.get(TranslateService);

    this.sendingToComPlusLoading = true;
    let xml: string;
    try {
      const project = currentService.HBMProject;
      xml = await kitchenService.getPrevisit(project.uuid);
    } catch (error: any) {
      this.sendingToComPlusLoading = false;
      alert(
        translateService.instant('validation.com_plus_error.previsit_create_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }

    const processQuoteService = this.injector.get(ProcessQuoteService);
    let kitchenPrevisitOpportunityNumber;
    try {
      kitchenPrevisitOpportunityNumber = await processQuoteService.sendDotComPlusOrder(xml);
    } catch (error: any) {
      this.sendingToComPlusLoading = false;
      alert(
        translateService.instant('validation.com_plus_error.previsit_send_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }

    this.sendingToComPlusLoading = false;
    alert(
      translateService.instant('validation.kitchen_previsit_sent', {
        opportunityNumber: kitchenPrevisitOpportunityNumber,
      })
    );
  }

  async orderPrevisitWorkbench() {
    if (!this.featureSettingsService.poseEnabled) {
      return;
    }

    const tunnelService = this.injector.get(TunnelService);
    const translateService = this.injector.get(TranslateService);
    let workbenchService = this.injector.get(WorkbenchService);

    this.sendingToComPlusLoading = true;
    let xml: string;
    try {
      xml = await workbenchService.getPrevisit(tunnelService.kitchenTunnelData.workbenchProjectUUID);
    } catch (error: any) {
      this.sendingToComPlusLoading = false;
      alert(
        translateService.instant('validation.com_plus_error.previsit_create_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }

    const processQuoteService = this.injector.get(ProcessQuoteService);
    let workbenchPrevisitOpportunityNumber;
    try {
      workbenchPrevisitOpportunityNumber = await processQuoteService.sendDotComPlusOrder(xml);
    } catch (error: any) {
      this.sendingToComPlusLoading = false;
      alert(
        translateService.instant('validation.com_plus_error.previsit_send_error', {
          error: JSON.stringify(error),
        })
      );
      return;
    }

    this.sendingToComPlusLoading = false;
    alert(
      translateService.instant('validation.workbench_previsit_sent', {
        opportunityNumber: workbenchPrevisitOpportunityNumber,
      })
    );
  }

  showProductList() {
    const tunnelService = this.injector.get(TunnelService);
    const modalService = this.injector.get(ModalService);
    modalService.display(MODAL.PRODUCTS_LIST_V2, {
      bom: tunnelService.kitchenTunnelData.bom,
      reloadBom: false,
      isReadOnly: true,
    });
  }

  async loadOffers(requestContent, withInstallation: boolean) {
    const kitchenService = this.injector.get(KitchenService);
    try {
      //const responseOffers = await kitchenService.postOffres(requestContent);
      //const offers = responseOffers.offers_data;
      /*if (Array.isArray(offers) && offers.length > 0) {
        const selectedOffer = offers.find(offer => offer.matching === 'offer_best');
        this.selectOffer(offers, selectedOffer, withInstallation);
      }*/
    } catch (error) {
      logError('Erreur lors du chargement des offres :', error);
    }
  }

  selectOffer(offers, selectedOffer, withInstallation: boolean) {
    const kitchenService = this.injector.get(KitchenService);
    const kitchenTunnelData = this.injector.get(TunnelService).kitchenTunnelData;
    if (selectedOffer) {
      this.handleSelectedOfferData(offers, selectedOffer, withInstallation);

      let requestContent = {
        offre_type: selectedOffer['bareme_type'],
        offre_gratuite: String(selectedOffer['coutcredit'] == 0),
      };

      // this.mlLoading = true;
      /* kitchenService.postMentionsLegale(requestContent).then(response => {
         withInstallation
           ? (kitchenTunnelData.fundingWithInstallation.mlData = response)
           : (kitchenTunnelData.fundingWithoutInstallation.mlData = response);
       });*/
    } else {
      this.handleDefaultSelectedOffer();
    }
  }

  private handleSelectedOfferData(offers, selectedOffer, withInstallation: boolean) {
    const kitchenTunnelData = this.injector.get(TunnelService).kitchenTunnelData;
    if (withInstallation) {
      kitchenTunnelData.fundingWithInstallation.offerTable = offers;
      kitchenTunnelData.fundingWithInstallation.duration = selectedOffer['nbecheances'];
      kitchenTunnelData.fundingWithInstallation.mensuality = selectedOffer['mens_1'];
      kitchenTunnelData.fundingWithInstallation.vac = selectedOffer['offre_details']['txt_exemple_vac'];
      kitchenTunnelData.fundingWithInstallation.txtAssurance = selectedOffer['offre_details']['txt_assurance'];
      kitchenTunnelData.fundingWithInstallation.interest = selectedOffer['coutcredit'];
      kitchenTunnelData.fundingWithInstallation.totalFundingCost =
        selectedOffer['totaldu'] + kitchenTunnelData.downPayment;
    } else {
      kitchenTunnelData.fundingWithoutInstallation.offerTable = offers;
      kitchenTunnelData.fundingWithoutInstallation.duration = selectedOffer['nbecheances'];
      kitchenTunnelData.fundingWithoutInstallation.mensuality = selectedOffer['mens_1'];
      kitchenTunnelData.fundingWithoutInstallation.vac = selectedOffer['offre_details']['txt_exemple_vac'];
      kitchenTunnelData.fundingWithoutInstallation.txtAssurance = selectedOffer['offre_details']['txt_assurance'];
      kitchenTunnelData.fundingWithoutInstallation.interest = selectedOffer['coutcredit'];
      kitchenTunnelData.fundingWithoutInstallation.totalFundingCost =
        selectedOffer['totaldu'] + kitchenTunnelData.downPayment;
    }
  }

  private handleDefaultSelectedOffer() {
    const kitchenTunnelData = this.injector.get(TunnelService).kitchenTunnelData;
    if (kitchenTunnelData.withInstallation) {
      kitchenTunnelData.fundingWithInstallation.duration = undefined;
      kitchenTunnelData.fundingWithInstallation.mensuality = undefined;
    } else {
      kitchenTunnelData.fundingWithoutInstallation.duration = undefined;
      kitchenTunnelData.fundingWithoutInstallation.mensuality = undefined;
    }
  }

  showDiscountChoice() {
    const tunnelService = this.injector.get(TunnelService);
    const modalService = this.injector.get(ModalService);
    modalService.display(MODAL.VALIDATION_DISCOUNT_CHOICE, {
      discountValueSelected: this.discountValueSelected,
      discountValue: tunnelService.kitchenTunnelData.discountValue,
    });
  }

  /**
   * Appel la méthode pour générer le pdf dans le back et appel la méthode pour l'afficher dans le navigateur.
   */
  async printValidationNew() {
    const kitchenService = this.injector.get(KitchenService);
    this.printLoading = true;
    const pdfViewModel: PdfViewModel = this.initPdfViewModel();
    try {
      const printService = this.injector.get(PrintService);
      const base64 = await kitchenService.generateNewPdf(pdfViewModel);
      printService.printPdf(base64.value, true);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.printLoading = false;
    }
  }

  private buildKitchenFinancementDto(): KitchenFinancementDto {
    const tunnelService = this.injector.get(TunnelService);
    return {
      downPayment: tunnelService.kitchenTunnelData.downPayment,
      withoutInstallation: {
        mensuality: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.mensuality,
        duration: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.duration,
        vac: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.vac,
        txtAssurance: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.txtAssurance,
        offers: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.offerTable,
        mlCasto: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.mlData?.ml_casto,
        mlSofinco: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.mlData?.ml_sofinco,
        interest: tunnelService.kitchenTunnelData.fundingWithoutInstallation?.interest,
      },
      withInstallation: {
        mensuality: tunnelService.kitchenTunnelData.fundingWithInstallation?.mensuality,
        duration: tunnelService.kitchenTunnelData.fundingWithInstallation?.duration,
        vac: tunnelService.kitchenTunnelData.fundingWithInstallation?.vac,
        txtAssurance: tunnelService.kitchenTunnelData.fundingWithInstallation?.txtAssurance,
        offers: tunnelService.kitchenTunnelData.fundingWithInstallation.offerTable,
        mlCasto: tunnelService.kitchenTunnelData.fundingWithInstallation?.mlData?.ml_casto,
        mlSofinco: tunnelService.kitchenTunnelData.fundingWithInstallation?.mlData?.ml_sofinco,
        interest: tunnelService.kitchenTunnelData.fundingWithInstallation?.interest,
      },
    };
  }

  checkFidIsUndefined() {
    if (!this.featureSettingsService.fidelityEnabled) {
      return false;
    }

    const tunnelService = this.injector.get(TunnelService);
    return (
      tunnelService.kitchenTunnelData.fidelityCardPoint === undefined ||
      tunnelService.kitchenTunnelData.fidelityCardPoint.length === 0
    );
  }

  checkFundingIsUndefined() {
    if (!this.featureSettingsService.financementEnabled) {
      return false;
    }

    const tunnelService = this.injector.get(TunnelService);
    return (
      tunnelService.kitchenTunnelData.fundingWithInstallation.offerTable === undefined ||
      tunnelService.kitchenTunnelData.fundingWithoutInstallation.offerTable === undefined
    );
  }

  private buildKitchenInstallationDto(): KitchenInstallationDto {
    const tunnelService = this.injector.get(TunnelService);
    const translateService = this.injector.get(TranslateService);
    return {
      installationDetails: tunnelService.kitchenTunnelData.detailTable,
      installationDisclaimer: translateService.instant('popins.installation.disclaimer'),
      genericApplianceWarningText: translateService.instant('popins.applianceWarning.text_print'),
    };
  }

  get context() {
    const customerService = this.injector.get(CustomerService);
    return customerService.context;
  }

  get featureWarningMessages(): string[] {
    const translateService = this.injector.get(TranslateService);
    const messages: string[] = [];

    if (!this.featureSettingsService.fidelityEnabled) {
      messages.push(translateService.instant('feature-settings.warning.fidelity'));
    }

    if (!this.featureSettingsService.poseEnabled) {
      messages.push(translateService.instant('feature-settings.warning.pose'));
    }

    if (!this.featureSettingsService.financementEnabled) {
      messages.push(translateService.instant('feature-settings.warning.financement'));
    }

    return messages;
  }

  /**
   * Initialise l'objet PdfViewModel
   * @returns
   */
  initPdfViewModel(): PdfViewModel {
    const tunnelService = this.injector.get(TunnelService);
    const translateService = this.injector.get(TranslateService);
    const customerService = this.injector.get(CustomerService);

    let bom = cloneDeep(tunnelService.kitchenTunnelData.bom);
    bom.totalByCategories.forEach(x => {
      x.key = translateService.instant('popins.products_list.main.' + x.key + '.title');
    });

    return {
      configContext: customerService.context,
      configurationId: tunnelService.kitchenTunnelData.configId,
      configurationId2D: tunnelService.workbenchTunnelData.id,
      customerContext: translateService.instant('popins.products_list.print_title_' + customerService.context),
      disclaimer: translateService.instant('popins.products_list.aside.disclaimerPdf'),
      rootBom: bom,
      // storageOfferConfiguration: null,
      kitchenOfferConfiguration: {
        isLess2Years: tunnelService.kitchenTunnelData.isLess2Years,
        discount: tunnelService.kitchenTunnelData.discountValue,
        kitchenPricing: tunnelService.kitchenTunnelData.kitchenPricing,
        kitchenFinancementDto: this.featureSettingsService.financementEnabled ? this.buildKitchenFinancementDto() : null,
        kitchenInstallationDto: this.featureSettingsService.poseEnabled ? this.buildKitchenInstallationDto() : null,
      },
      ProjectImageUrl: this.model.projectImageUrl,
      fidelityCardPoints: this.featureSettingsService.fidelityEnabled
        ? tunnelService.kitchenTunnelData.fidelityCardPoint
        : [],
      currentElements: {
        // Pas besoin de translate pour le model.
        model: this.model.inspiration.currentModel ? this.model.inspiration.currentModel : '',
        layout: this.model.inspiration.currentLayout
          ? translateService.instant(this.model.inspiration.currentLayout)
          : '',
        ovenConfiguration: this.model.inspiration.currentOvenConfiguration
          ? translateService.instant(this.model.inspiration.currentOvenConfiguration)
          : '',
        fridgeConfiguration: this.model.inspiration.currentFridgeConfiguration
          ? translateService.instant(this.model.inspiration.currentFridgeConfiguration)
          : '',
      },
    };
  }

  private get priceKey() {
    const tunnelService = this.injector.get(TunnelService);
    return tunnelService?.kitchenTunnelData?.isLess2Years ? 'moins2' : 'plus2';
  }

  private showSuccessToast(message: string) {
    const toastService = this.injector.get(ToastService);
    const translateService = this.injector.get(TranslateService);

    toastService.showSuccessToast(translateService.instant('validation.toast.success_title'), message);
  }

  private showErrorToast(message: string) {
    const toastService = this.injector.get(ToastService);
    const translateService = this.injector.get(TranslateService);

    toastService.showErrorToast(translateService.instant('validation.toast.error_title'), message);
  }
}
